/**
 * `MaskedField` - click-to-reveal PII primitive.
 *
 * Renders a `<button>` (so screen readers announce it as button + pressed
 * state) wrapped around the masked value plus a lock-icon affordance.
 * Reveal flow:
 *
 *   1. Default: masked text + closed-lock icon. `aria-pressed=false`.
 *      Accessible name names the field and warns the user the click is
 *      audited.
 *   2. Click (or keyboard Enter / Space): button enters `revealing`
 *      state, disabled, spinner replaces icon. The reveal-audit POST is
 *      in flight.
 *   3. On 2xx: button repaints in the `revealed` state with the soft-amber
 *      surface from the MUR "Design Tokens and Accessibility" page (`bg-revealed`), open-lock icon,
 *      `aria-pressed=true`. The plaintext value renders inside.
 *   4. On non-2xx: button stays in `error` state; inline message offers
 *      Retry. Field remains masked. Correlation id is surfaced for ops.
 *
 * Re-mask paths (auto-re-mask on 30s inactivity, tab blur, navigation,
 * or user-pressed-again) are owned by `usePiiReveal`.
 *
 * Accessibility (WCAG 2.2 AA, per ADR-013 screen-reader semantics):
 *   - Real `<button>` with `aria-pressed`.
 *   - `aria-live="polite"` companion region announces "<field name>
 *     revealed" with NO PII in the announcement.
 *   - Lock icon flips (closed -> open) so colour is not the only signal.
 *   - Focus stays on the button after reveal (no focus relocation).
 */

import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'

import type { BffClient, PiiField, PiiResource } from '../lib/bff-client'
import { formatMasked } from '../lib/mask'
import { FADE_LEAD_MS, usePiiReveal } from '../features/accounts/detail/use-pii-reveal'
import { FOCUS_RING_CLASS } from './focus-ring'
import { LockClosedIcon, LockOpenIcon } from './lock-icon'
import { Spinner } from './spinner'
import { useOptionalToast } from './toast'

export interface MaskedFieldProps {
  readonly client: BffClient
  /** Resource id - account UUID or holder UUID; carried on the audit event. */
  readonly accountId: string
  readonly field: PiiField
  /**
   * Resource the reveal targets (SM2-7). Defaults to `account`; the
   * holder region passes `holder` so the audit emits
   * `murmuration.holder.pii_revealed`. The masking model is otherwise identical.
   */
  readonly resource?: PiiResource
  /** Plain value from the BFF response. Null is rendered as `n/a`. */
  readonly value: string | null
  /** Display label - shown via the button's accessible name and the announce region. */
  readonly label: string
  readonly onAuthError?: ((err: unknown) => void) | undefined
}

export function MaskedField({
  client,
  accountId,
  field,
  resource,
  value,
  label,
  onAuthError,
}: MaskedFieldProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const announceId = useId()
  const hintId = `${announceId}-hint`
  // Tracks whether this field has ever been revealed, so the live region
  // stays silent on load but can announce the re-mask afterwards.
  const hasRevealedRef = useRef(false)

  const { state, correlationId, fading, reveal, mask, clearError } = usePiiReveal({
    client,
    accountId,
    field,
    resource,
    containerRef,
    onAuthError,
  })

  const toast = useOptionalToast()
  // Surface a polite toast the moment the fade pre-warning begins so the
  // user has a chance to interact and reset the idle timer before the
  // auto-re-mask fires. Only fires on the transition into `fading`, not
  // every render.
  useEffect(() => {
    if (fading) {
      const seconds = Math.round(FADE_LEAD_MS / 1000)
      toast.show({ message: `${label} will hide in ${seconds} seconds`, kind: 'status' })
    }
  }, [fading, label, toast])

  if (value === null) {
    return (
      <span data-pii-field={field} className="text-text-muted">
        n/a
      </span>
    )
  }

  const masked = formatMasked(field, value) ?? '(hidden)'
  const isRevealed = state === 'revealed'
  const isRevealing = state === 'revealing'
  const isError = state === 'error'

  const accessibleName = isRevealed
    ? `${label}, revealed`
    : `Reveal ${label}. This action will be audited.`

  const handleClick = (): void => {
    // The button stays enabled while the audit emit is in flight (see the
    // aria-busy note below), so the in-flight guard lives here instead.
    if (isRevealing) return
    if (isError) {
      // Re-attempting from the error state - clear, then retry.
      clearError()
    }
    if (isRevealed) {
      mask('user')
      return
    }
    void reveal()
  }

  // The button DOM intentionally contains only non-PII text:
  //   - masked state: the mask itself (no plaintext)
  //   - revealing/error state: the mask itself (no plaintext)
  //   - revealed state: the plaintext (the user asked for it and the
  //     audit emit returned 2xx).
  const visible: ReactNode = isRevealed ? <span data-testid="revealed-value">{value}</span> : masked

  if (isRevealed) hasRevealedRef.current = true
  const announcement = isRevealed
    ? `${label} revealed`
    : hasRevealedRef.current
      ? `${label} hidden`
      : ''

  return (
    <div
      ref={containerRef}
      className="inline-flex flex-col gap-murmuration-1"
      data-pii-container={field}
    >
      <button
        ref={buttonRef}
        type="button"
        data-pii-field={field}
        data-pii-state={state}
        data-pii-fading={fading || undefined}
        aria-pressed={isRevealed}
        aria-busy={isRevealing}
        aria-label={accessibleName}
        aria-describedby={hintId}
        onClick={handleClick}
        className={[
          'inline-flex items-center gap-murmuration-2 rounded-sm px-murmuration-2 py-murmuration-1 font-mono text-murmuration-code',
          'border border-border-default transition-all duration-murmuration-fast',
          FOCUS_RING_CLASS,
          isRevealed
            ? 'bg-bg-revealed text-text-primary border-status-warning'
            : 'bg-bg-surface-2 text-text-secondary hover:bg-bg-surface',
          isError ? 'border-status-danger' : '',
          // Fade affordance: opacity ramp during the lead-in to auto-re-mask.
          // CSS transition is on `opacity` (1s ease) so the user sees a
          // smooth dim rather than a step.
          fading ? 'opacity-40 [transition-duration:1000ms]' : 'opacity-100',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isRevealing ? (
          <Spinner />
        ) : isRevealed ? (
          <LockOpenIcon className="text-status-warning" />
        ) : (
          <LockClosedIcon className="text-text-muted" />
        )}
        <span>{visible}</span>
      </button>

      {/*
        Polite announcement region - never contains PII.

        Announces both directions: ADR-013 specifies "Bank account
        number hidden" on re-mask, not just the reveal. Empty until the
        first reveal, so a page of masked fields does not announce anything
        on load.

        Separate from the button's own description (`hintId`) - one element
        cannot sensibly be both a live region and a static description.
      */}
      <span id={announceId} aria-live="polite" className="sr-only">
        {announcement}
      </span>

      {/* Static description, referenced by the button. Explains the audit
          consequence to screen-reader users on focus. */}
      <span id={hintId} className="sr-only">
        Revealing this value is recorded against your staff identity.
      </span>

      {isError && (
        <span role="status" className="text-murmuration-label text-status-danger">
          Could not reveal - try again.{' '}
          {correlationId !== null && correlationId !== '' && (
            <>
              Correlation id: <code className="select-all font-mono">{correlationId}</code>
            </>
          )}
        </span>
      )}
    </div>
  )
}
